import './ToolsTreeModal.less';

import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Modal, Button, Form, Tooltip, Alert, Switch, Divider } from 'antd';
import type { SwitchProps } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { OrgEnvironmentType } from '../../enums/organization';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import {
  ToolConfig,
  ToolConfigDataAccess,
  ToolConfigDataTypeAccess,
  ToolConfigType,
} from '../../interfaces/toolConfig';
import {
  calculateToolConfigDataAccess,
  getAccessLevelName,
  getAppliedToolConfig,
  getFallbackToolConfig,
  getParentToolConfigs,
  getRestrictionTooltip,
  isOptionRestricted,
} from '../../services/toolConfigService';
import { Notify } from '../../services/notificationService';
import {
  createToolConfig,
  updateToolConfig,
} from '../../app/ActionTypes/organizationActions';
import { RootState, AppDispatch } from '../../app/store';
import { getErrorMessage } from '../../utils/errorUtils';
import { useAbility } from '../../hooks/useAbility';
import { Resource } from '../../enums/common';
import DataAccessLevelFormItems from './DataAccessLevelFormItems';
import { useUserAccess } from '../../hooks/useUserAccess';

interface ToolsTreeModalProps {
  envType: OrgEnvironmentType;
  title?: string;
  toolConfigs: ToolConfig[];
  cirraUserId: string | null;
  cirraOrganizationId: string | null;
  sfOrgId: string | null;
  resetLabel?: string;

  readOnlyDataAccess?: ToolConfigDataAccess;
  readOnlyDataAccessMessage?: string;
}

const _getDataAccessFromFormValues = (formValues: ToolConfigForm) => {
  return _.omit(
    formValues,
    'is_overridable',
    'is_connection_creation_restricted',
    'is_connection_creation_restricted_for_sub_orgs'
  );
};
interface ToolConfigForm {
  is_overridable?: boolean;
  is_connection_creation_restricted?: boolean;
  is_connection_creation_restricted_for_sub_orgs?: boolean;
  metadata: ToolConfigDataTypeAccess;
  user_data: ToolConfigDataTypeAccess;
  personal_setup_data: ToolConfigDataTypeAccess;
  business_data: ToolConfigDataTypeAccess;
}

interface UpdateToolConfigPayload {
  id: string;
  data_access: ToolConfigDataAccess;
  is_overridable: boolean;
  is_connection_creation_restricted: boolean;
  is_connection_creation_restricted_for_sub_orgs: boolean;
}

interface CreateToolConfigPayload {
  data_access: ToolConfigDataAccess;
  is_overridable: boolean;
  is_connection_creation_restricted: boolean;
  is_connection_creation_restricted_for_sub_orgs: boolean;
  salesforce_environment: OrgEnvironmentType;
  cirra_user_id: string | null;
  cirra_organization_id: string | null;
  salesforce_organization_id: string | null;
}

// Wrapper component to allow tooltip on Switch while maintaining Form.Item control
interface SwitchWithTooltipProps extends SwitchProps {
  tooltip?: string;
}
const SwitchWithTooltip = React.forwardRef<
  HTMLButtonElement,
  SwitchWithTooltipProps
>((props, ref) => {
  const { tooltip, disabled, ...switchProps } = props;
  return (
    <div
      style={{
        ...(props.style || {}),
        width: 'fit-content',
        display: 'inline-block',
      }}
    >
      <Tooltip title={tooltip}>
        <span>
          <Switch {...switchProps} ref={ref} size="small" disabled={disabled} />
        </span>
      </Tooltip>
    </div>
  );
});
SwitchWithTooltip.displayName = 'SwitchWithTooltip';

interface SwitchFormItemProps {
  name: string;
  label: string;
  labelTooltip: string;
  switchTooltip: string;
  switchExplanation: string;
  tall?: boolean;
  disabled?: boolean;
  hidden?: boolean;
}

const SwitchFormItem: React.FC<SwitchFormItemProps> = ({
  name,
  label,
  labelTooltip,
  switchTooltip,
  switchExplanation,
  tall = false,
  disabled = false,
  hidden = false,
}) => {
  return (
    <Form.Item
      className={`form-item ${hidden ? 'hidden' : ''}`}
      label={
        <div className="form-label">
          <div>{label}</div>
          <div>
            &nbsp;
            <InfoTooltip content={labelTooltip} />
          </div>
        </div>
      }
    >
      <Form.Item
        className={tall ? 'tall' : ''}
        name={name}
        valuePropName="checked"
      >
        <SwitchWithTooltip tooltip={switchTooltip} disabled={disabled} />
      </Form.Item>
      <div className="explanation">{switchExplanation}</div>
    </Form.Item>
  );
};

const ToolsTreeModal: React.FC<ToolsTreeModalProps> = ({
  envType,
  title,
  toolConfigs: allToolConfigs,
  cirraUserId,
  cirraOrganizationId,
  sfOrgId,
  resetLabel,
  readOnlyDataAccess,
  readOnlyDataAccessMessage,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isTeamUser, currentTeamAccount } = useUserAccess();

  const [toolConfigs, setToolConfigs] = useState<ToolConfig[]>([]);
  const [masterTeamToolConfigs, setMasterTeamToolConfigs] = useState<
    ToolConfig[]
  >([]);
  useEffect(() => {
    const masterTeamCirraId =
      currentTeamAccount?.team?.master_organization?.cirra_organization_id;
    if (masterTeamCirraId) {
      const splitToolConfigs = _.partition(
        allToolConfigs,
        (toolConfig) => toolConfig.cirra_organization_id === masterTeamCirraId
      );
      setMasterTeamToolConfigs(splitToolConfigs[0]);
      setToolConfigs(splitToolConfigs[1]);
    } else {
      setMasterTeamToolConfigs([]);
      setToolConfigs(allToolConfigs);
    }
  }, [
    allToolConfigs,
    currentTeamAccount?.team?.master_organization?.cirra_organization_id,
  ]);

  const masterTeamToolConfigForEnvType = masterTeamToolConfigs.find(
    (toolConfig) => toolConfig.salesforce_environment === envType
  );

  const defaultIsConnectionCreationRestricted = false;
  const isConnectionCreationRestrictedByMasterTeam =
    masterTeamToolConfigForEnvType?.is_connection_creation_restricted &&
    masterTeamToolConfigForEnvType?.is_connection_creation_restricted_for_sub_orgs;

  const isSavingToolConfig = useSelector(
    (state: RootState) => state.organization.isSavingToolConfig
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm<ToolConfigForm>();

  const [appliedToolConfig, setAppliedToolConfig] = useState<ToolConfig | null>(
    null
  );
  const [fallbackToolConfig, setFallbackToolConfig] =
    useState<ToolConfig | null>(null);

  const currentToolConfigType = sfOrgId
    ? ToolConfigType.UserOrganization
    : ToolConfigType.UserEnvironment;

  const isCreatingNewToolConfig =
    appliedToolConfig?.type !== currentToolConfigType;
  const isUpdatingToolConfig =
    appliedToolConfig?.type === currentToolConfigType;

  const { currentAbility } = useAbility();
  const tentativeToolConfigData = {
    type: currentToolConfigType,
    salesforce_environment: envType,
    cirra_user_id:
      cirraOrganizationId &&
      currentToolConfigType === ToolConfigType.UserEnvironment
        ? null
        : cirraUserId,
    cirra_organization_id: cirraOrganizationId,
    data_access: {},
    salesforce_organization_id: sfOrgId,
  };

  const hasPermissionsToCreate = currentAbility.can(
    'create',
    Resource.ToolConfig,
    tentativeToolConfigData
  );
  const hasPermissionsToUpdate = currentAbility.can(
    'update',
    Resource.ToolConfig,
    tentativeToolConfigData
  );
  const hasPermissions =
    (isCreatingNewToolConfig && hasPermissionsToCreate) ||
    (isUpdatingToolConfig && hasPermissionsToUpdate);

  const insufficientPermissionsMessage = `You don't have permissions to update category tool settings`;

  const parentToolConfigs = useMemo(() => {
    return getParentToolConfigs({
      toolConfigs,
      type: currentToolConfigType,
      sfOrgEnvironment: envType,
    });
  }, [toolConfigs, currentToolConfigType, envType]);

  const parentDataAccess = useMemo(
    () => calculateToolConfigDataAccess(parentToolConfigs),
    [parentToolConfigs]
  );

  useEffect(() => {
    const _appliedToolConfig = getAppliedToolConfig({
      toolConfigs,
      sfOrgEnvironment: envType,
      cirraUserId: cirraUserId,
      cirraOrganizationId: cirraOrganizationId,
      sfOrgId: sfOrgId,
    });

    const _fallbackToolConfig = getFallbackToolConfig({
      toolConfigs,
      sfOrgEnvironment: envType,
      cirraUserId: cirraUserId,
      cirraOrganizationId: cirraOrganizationId,
      sfOrgId: sfOrgId,
    });

    setAppliedToolConfig(_appliedToolConfig);
    setFallbackToolConfig(_fallbackToolConfig);
  }, [toolConfigs, envType, cirraUserId, cirraOrganizationId, sfOrgId]);

  const buildFormState = (toolConfig: ToolConfig | null): ToolConfigForm => {
    // If we have a tool config, merge it with parent configs to respect restrictions
    if (toolConfig) {
      const allConfigs = [...parentToolConfigs, toolConfig];
      const mergedDataAccess = calculateToolConfigDataAccess(allConfigs);
      return {
        is_overridable: toolConfig.is_overridable ?? true,
        is_connection_creation_restricted:
          (isConnectionCreationRestrictedByMasterTeam ||
            toolConfig.is_connection_creation_restricted) ??
          defaultIsConnectionCreationRestricted,
        is_connection_creation_restricted_for_sub_orgs:
          toolConfig.is_connection_creation_restricted_for_sub_orgs ?? false,
        ...mergedDataAccess,
      };
    }

    // Default state for new configs
    const defaultDataAccess = {
      metadata: ToolConfigDataTypeAccess.None,
      user_data: ToolConfigDataTypeAccess.None,
      personal_setup_data: ToolConfigDataTypeAccess.None,
      business_data: ToolConfigDataTypeAccess.None,
    };
    return {
      is_overridable: true,
      is_connection_creation_restricted:
        isConnectionCreationRestrictedByMasterTeam ||
        defaultIsConnectionCreationRestricted,
      is_connection_creation_restricted_for_sub_orgs: false,
      ...defaultDataAccess,
    };
  };

  const defaultFormState = useMemo(() => {
    if (readOnlyDataAccess) {
      return {
        is_overridable: false,
        is_connection_creation_restricted:
          isConnectionCreationRestrictedByMasterTeam ||
          defaultIsConnectionCreationRestricted,
        is_connection_creation_restricted_for_sub_orgs: false,
        ...readOnlyDataAccess,
      };
    }

    // If we have an applied tool config, use its actual values
    if (appliedToolConfig) {
      return buildFormState(appliedToolConfig);
    }

    // Otherwise, use values from parent configs (for new configs)
    return {
      is_overridable: true, // Default for new configs
      is_connection_creation_restricted:
        isConnectionCreationRestrictedByMasterTeam ||
        defaultIsConnectionCreationRestricted,
      is_connection_creation_restricted_for_sub_orgs: false,
      ...parentDataAccess,
    };
  }, [
    toolConfigs,
    appliedToolConfig,
    currentToolConfigType,
    parentDataAccess,
    readOnlyDataAccess,
    isConnectionCreationRestrictedByMasterTeam,
    parentToolConfigs,
  ]);

  const formValues = Form.useWatch([], form);
  const allowOverrideValue = Form.useWatch('is_overridable', form);
  const restrictConnectionCreationValue = Form.useWatch(
    'is_connection_creation_restricted',
    form
  );
  const applyToSubTeamsValue = Form.useWatch(
    'is_connection_creation_restricted_for_sub_orgs',
    form
  );

  // Reset "Apply to sub teams" when "Restrict connection creation" is toggled off
  useEffect(() => {
    if (restrictConnectionCreationValue === false) {
      form.setFieldsValue({
        is_connection_creation_restricted_for_sub_orgs: false,
      });
    }
  }, [restrictConnectionCreationValue, form]);

  const isFormDifferentFromDefault = useMemo(() => {
    if (!formValues) return false;

    if (
      currentToolConfigType === ToolConfigType.UserEnvironment &&
      formValues?.is_overridable !== defaultFormState?.is_overridable
    ) {
      return true;
    }
    if (
      formValues?.is_connection_creation_restricted !==
      defaultFormState?.is_connection_creation_restricted
    ) {
      return true;
    }
    if (
      formValues?.is_connection_creation_restricted_for_sub_orgs !==
      defaultFormState?.is_connection_creation_restricted_for_sub_orgs
    ) {
      return true;
    }
    return !_.isEqual(
      _getDataAccessFromFormValues(formValues),
      _getDataAccessFromFormValues(defaultFormState)
    );
  }, [defaultFormState, formValues, currentToolConfigType]);

  const isFormDifferentFromFallback = useMemo(() => {
    if (!formValues) return false;

    // Compare the current state with the default state. Ignore is_overridable.
    const fallbackFormState = buildFormState(fallbackToolConfig);
    const fallbackDataAccess = _getDataAccessFromFormValues(fallbackFormState);
    const currentState = {
      ..._.pick(formValues, [
        'business_data',
        'metadata',
        'personal_setup_data',
        'user_data',
      ]),
      is_connection_creation_restricted:
        formValues.is_connection_creation_restricted,
      is_connection_creation_restricted_for_sub_orgs:
        formValues.is_connection_creation_restricted_for_sub_orgs,
    };
    const fallbackState = {
      ...fallbackDataAccess,
      is_connection_creation_restricted:
        fallbackFormState.is_connection_creation_restricted,
      is_connection_creation_restricted_for_sub_orgs:
        fallbackFormState.is_connection_creation_restricted_for_sub_orgs,
    };

    return !_.isEqual(currentState, fallbackState);
  }, [
    fallbackToolConfig,
    formValues,
    restrictConnectionCreationValue,
    applyToSubTeamsValue,
    parentToolConfigs,
    isConnectionCreationRestrictedByMasterTeam,
  ]);

  const showModal = () => {
    setIsModalVisible(true);
    form.setFieldsValue({
      ...defaultFormState,
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleReset = () => {
    form.setFieldsValue({ ...buildFormState(fallbackToolConfig) });
  };

  const handleSave = async () => {
    try {
      const formValues = form.getFieldsValue();
      const newDataAccess = _.omit(
        formValues,
        'is_overridable',
        'is_connection_creation_restricted',
        'is_connection_creation_restricted_for_sub_orgs'
      );
      const newIsOverridable = formValues.is_overridable || false;
      const newRestrictConnectionCreation =
        formValues.is_connection_creation_restricted || false;
      // Ensure consistency: if connection creation is not restricted,
      // then it cannot be restricted for sub orgs
      const newApplyToSubTeams =
        newRestrictConnectionCreation &&
        (formValues.is_connection_creation_restricted_for_sub_orgs || false);

      const baseToolConfig = {
        data_access: newDataAccess,
        is_overridable: newIsOverridable,
        is_connection_creation_restricted: newRestrictConnectionCreation,
        is_connection_creation_restricted_for_sub_orgs: newApplyToSubTeams,
      };
      if (
        appliedToolConfig &&
        appliedToolConfig.type === currentToolConfigType
      ) {
        // Update existing tool config
        await dispatch(
          updateToolConfig({
            ...baseToolConfig,
            id: appliedToolConfig.id,
          } as UpdateToolConfigPayload)
        ).unwrap();
      } else {
        // Create new tool config (either when appliedToolConfig is null or has different type)
        await dispatch(
          createToolConfig({
            ...baseToolConfig,
            salesforce_environment: envType,
            cirra_user_id: cirraUserId,
            cirra_organization_id: cirraOrganizationId,
            salesforce_organization_id: sfOrgId,
          } as CreateToolConfigPayload)
        ).unwrap();
      }

      Notify.success('Tool configuration saved successfully');
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error saving tool config:', error);
      const userErrorMsg = getErrorMessage({
        error,
        resourceName: 'tool configuration',
        defaultMessage: 'Failed to save tool configuration',
      });
      Notify.error(userErrorMsg);
    }
  };

  let tooltipForUpdate = '';
  if (!hasPermissions) {
    tooltipForUpdate = insufficientPermissionsMessage;
  } else if (readOnlyDataAccessMessage) {
    tooltipForUpdate = readOnlyDataAccessMessage;
  }

  let tooltipForReset = '';
  if (!hasPermissionsToUpdate) {
    tooltipForReset = insufficientPermissionsMessage;
  } else if (readOnlyDataAccessMessage) {
    tooltipForReset = readOnlyDataAccessMessage;
  }

  return (
    <div className="tools-tree-modal-trigger-container">
      <Button
        className="tools-tree-modal-trigger-button"
        type="link"
        onClick={showModal}
      >
        <SettingOutlined />
      </Button>

      <Modal
        className="tools-tree-modal-body"
        title={title || `Tool settings for ${envType} orgs`}
        open={isModalVisible}
        onCancel={handleCancel}
        forceRender
        footer={[
          <div key="footer" className="modal-footer">
            <Tooltip title={tooltipForReset}>
              <Button
                type="link"
                disabled={
                  !hasPermissionsToUpdate ||
                  !fallbackToolConfig ||
                  !isFormDifferentFromFallback ||
                  isSavingToolConfig
                }
                onClick={handleReset}
              >
                {resetLabel || 'Reset to default'}
              </Button>
            </Tooltip>

            <Tooltip title={tooltipForUpdate}>
              <Button
                data-testid={`save-button-${envType}-${sfOrgId}`}
                type="primary"
                disabled={
                  !hasPermissions ||
                  !isFormDifferentFromDefault ||
                  isSavingToolConfig
                }
                loading={isSavingToolConfig}
                onClick={handleSave}
              >
                Save
              </Button>
            </Tooltip>
          </div>,
        ]}
        width={500}
      >
        <div className="modal-content">
          {!hasPermissions && (
            <Alert
              className="modal-alert"
              message={insufficientPermissionsMessage}
              type="warning"
              showIcon
            />
          )}

          {readOnlyDataAccessMessage && (
            <Alert
              className="modal-alert"
              message={readOnlyDataAccessMessage}
              type="warning"
              showIcon
            />
          )}

          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 10 }}
            wrapperCol={{ span: 14 }}
            className="form-container"
            disabled={!hasPermissions}
            initialValues={defaultFormState}
          >
            {!!parentToolConfigs?.length && (
              <DataAccessLevelFormItems
                dataTestId={`select-${envType}-${sfOrgId}`}
                getOption={(
                  level: ToolConfigDataTypeAccess,
                  dataTypeOpt: { key: string; label: string; info: string }
                ) => {
                  const _disabled = isOptionRestricted({
                    level,
                    dataType: dataTypeOpt.key as keyof ToolConfigDataAccess,
                    parentToolConfigs,
                  });

                  return {
                    value: level,
                    label: getAccessLevelName(level),
                    disabled: _disabled,
                    tooltip: _disabled
                      ? getRestrictionTooltip({
                          dataType:
                            dataTypeOpt.key as keyof ToolConfigDataAccess,
                          parentDataAccess,
                          envType,
                        })
                      : '',
                  };
                }}
              />
            )}
            {readOnlyDataAccess && (
              <DataAccessLevelFormItems
                dataTestId={`select-${envType}-${sfOrgId}`}
                disabled
                getOption={(level: ToolConfigDataTypeAccess) => {
                  return {
                    value: level,
                    label: getAccessLevelName(level),
                    disabled: false,
                    tooltip: '',
                  };
                }}
              />
            )}
            {currentToolConfigType === ToolConfigType.UserEnvironment && (
              <SwitchFormItem
                name="is_overridable"
                label="Allow override?"
                labelTooltip="When override is allowed, these restrictions can be relaxed for individual orgs."
                switchTooltip={
                  allowOverrideValue
                    ? 'Disable to disallow more lenient settings from individual orgs'
                    : 'Enable to allow more lenient settings for individual orgs'
                }
                switchExplanation={
                  allowOverrideValue
                    ? 'Restrictions can be relaxed at org level'
                    : 'Restrictions are enforced for all orgs'
                }
                disabled={!hasPermissionsToUpdate}
              />
            )}

            {isTeamUser &&
              currentToolConfigType === ToolConfigType.UserEnvironment && (
                <Divider className="divider " />
              )}
            <Form.Item
              className={`form-item ${!isTeamUser || currentToolConfigType !== ToolConfigType.UserEnvironment ? 'hidden' : ''}`}
              label={
                <div className="form-label">
                  <div>Allow connection creation by members?</div>
                  <div>
                    &nbsp;
                    <InfoTooltip content="When enabled, regular members can create new Salesforce connections." />
                  </div>
                </div>
              }
            >
              <Form.Item
                className="tall"
                name="is_connection_creation_restricted"
                valuePropName="checked"
                getValueFromEvent={(checked) => !checked}
                getValueProps={(value) => ({ checked: !value })}
              >
                <SwitchWithTooltip
                  tooltip={
                    isConnectionCreationRestrictedByMasterTeam
                      ? 'The restriction is enforced by the master team'
                      : !restrictConnectionCreationValue
                        ? 'Disable to restrict connection creation to admins only'
                        : 'Enable to allow regular members to create connections'
                  }
                  disabled={
                    !hasPermissionsToUpdate ||
                    isConnectionCreationRestrictedByMasterTeam
                  }
                />
              </Form.Item>
              <div className="explanation">
                {!restrictConnectionCreationValue
                  ? 'Regular members can create new Salesforce connections'
                  : 'Only admins can create new Salesforce connections'}
              </div>
            </Form.Item>
            <SwitchFormItem
              name="is_connection_creation_restricted_for_sub_orgs"
              label="Apply to sub teams?"
              labelTooltip="When enabled, these settings apply to all sub teams."
              switchTooltip={
                applyToSubTeamsValue
                  ? 'Disable to allow sub teams to relax restrictions'
                  : 'Enable to enforce restrictions for all sub teams'
              }
              switchExplanation={
                applyToSubTeamsValue
                  ? 'Restrictions cannot be relaxed at sub teams'
                  : 'Restrictions can be relaxed at sub teams'
              }
              disabled={!hasPermissionsToUpdate}
              hidden={
                !isTeamUser ||
                currentToolConfigType !== ToolConfigType.UserEnvironment ||
                !!currentTeamAccount?.team?.master_organization_id ||
                !restrictConnectionCreationValue
              }
            />
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default ToolsTreeModal;
